import { Listbox, Transition } from '@headlessui/react';
import { CaretDown, CaretUp, Check } from '@phosphor-icons/react';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type Option = {
  value: string;
  label?: string;
};

type MultiSelectBoxProps = {
  options: Option[];
  defaultValue?: Option[];
  variant?: 'outlined' | 'contained';
  color?: 'primary' | 'secondary' | 'default';
  size?: 'small' | 'medium' | 'large';
  className?: string;
  onChange?: (values: string[]) => void;
  disabled?: boolean;
  direction?: 'up' | 'down';
};

const variantClasses = {
  contained: {
    primary: 'bg-primary text-neutral-0 hover:opacity-90',
    secondary: 'bg-neutral-400 text-neutral-0 hover:bg-neutral-500',
    default: 'bg-neutral-400 text-neutral-0 hover:bg-neutral-500',
  },
  outlined: {
    primary: 'border hover:border-primary',
    secondary:
      'border border-neutral-400 text-neutral-400 hover:bg-neutral-400',
    default: 'border border-neutral-400 text-neutral-400 hover:bg-neutral-400',
  },
  text: {
    primary: 'text-primary hover:bg-primary hover:text-neutral-0',
    secondary: 'text-neutral-400 hover:bg-neutral-400 hover:text-neutral-0',
    default: 'text-neutral-400 hover:bg-neutral-400 hover:text-neutral-0',
  },
};

const activeClasses = {
  contained: {
    primary: 'bg-primary text-neutral-0 opacity-90',
    secondary: 'bg-neutral-500 text-neutral-0',
    default: 'bg-neutral-500 text-neutral-0',
  },
  outlined: {
    primary: 'border border-primary text-primary',
    secondary: 'border border-neutral-400 text-neutral-0 bg-neutral-400',
    default: 'border border-neutral-400 text-neutral-0 bg-neutral-400',
  },
  text: {
    primary: 'bg-primary text-neutral-0',
    secondary: 'bg-neutral-400 text-neutral-0',
    default: 'bg-neutral-400 text-neutral-0',
  },
};

const sizeClasses = {
  small: 'px-2 py-1 text-sm leading-6',
  medium: 'px-3 py-2.5 text-base leading-6',
  large: 'px-4 py-3 text-lg leading-7',
};

export const MultiSelectBox = ({
  options,
  defaultValue = [],
  variant = 'outlined',
  color = 'primary',
  size = 'medium',
  className = '',
  onChange,
  disabled = false,
  direction = 'down',
}: MultiSelectBoxProps) => {
  const [selected, setSelected] = useState<Option[]>(defaultValue);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const listboxRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    updatePosition();
  }, [selected]);

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const itemHeight = 40;
      const dropdownHeight = options.length * itemHeight;

      setPosition({
        top: direction === 'up' ? rect.top - dropdownHeight : rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    }
  };

  const handleSelection = (option: Option) => {
    const isSelected = selected.find((item) => item.value === option.value);
    let updatedSelection;

    if (isSelected) {
      updatedSelection = selected.filter((item) => item.value !== option.value);
    } else {
      updatedSelection = [...selected, option];
    }

    setSelected(updatedSelection);
    onChange?.(updatedSelection.map((item) => item.value));
  };

  const variantClass = variantClasses[variant][color];
  const sizeClass = sizeClasses[size];

  const maxHeight = 270;

  return (
    <Listbox
      value={selected}
      onChange={() => {}}
      multiple
      disabled={disabled || options.length === 0}
    >
      {({ open }) => (
        <div className="relative">
          <Listbox.Button
            ref={buttonRef}
            onClick={updatePosition}
            className={clsx(
              'w-full rounded-md border border-neutral-200',
              open ? activeClasses[variant][color] : variantClass,
              `${variantClass} ${sizeClass}`,
              'flex justify-between items-center leading-6',
              'focus:outline-none',
              className,
              disabled && 'opacity-50 cursor-not-allowed',
              options.length === 0 && 'opacity-50 cursor-not-allowed',
            )}
          >
            <div className="flex-1 text-left truncate">
              {selected.length > 0 ? (
                selected.map((item) => item.label || item.value).join(', ')
              ) : (
                <i>Select values...</i>
              )}
            </div>
            {direction === 'up' ? (
              <CaretUp className="ml-2 flex-shrink-0" />
            ) : (
              <CaretDown className="ml-2 flex-shrink-0" />
            )}
          </Listbox.Button>

          <Transition
            as={'div'}
            enter="transition ease-out duration-100"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            {createPortal(
              <Listbox.Options
                ref={listboxRef}
                className={clsx(
                  'absolute z-[9999] bg-neutral-0 shadow-lg rounded-md py-1',
                  'overflow-auto focus:outline-none text-sm',
                  'border border-neutral-100',
                )}
                style={{
                  top: `${position.top}px`,
                  left: `${position.left}px`,
                  width: `${position.width}px`,
                  position: 'fixed',
                  maxHeight: `${maxHeight}px`,
                }}
              >
                {options.map((option) => (
                  <Listbox.Option
                    key={option.value}
                    value={option}
                    className={({ active }) =>
                      clsx(
                        'cursor-pointer select-none relative py-3 px-4 m-2 rounded-lg truncate',
                        active && !option?.value && 'bg-neutral-100',
                        selected.some((item) => item.value === option.value) &&
                          'bg-neutral-100',
                        selected.some((item) => item.value === option.value) &&
                          'font-bold',
                        'text-neutral-600',
                        'hover:bg-neutral-50',
                        'active:bg-neutral-100',
                      )
                    }
                    onClick={() => handleSelection(option)}
                  >
                    <div className="flex justify-between items-center">
                      <span>{option.label || option.value}</span>
                      {selected.some((item) => item.value === option.value) && (
                        <Check className="w-5 h-5" />
                      )}
                    </div>
                  </Listbox.Option>
                ))}
              </Listbox.Options>,
              document.body,
            )}
          </Transition>
        </div>
      )}
    </Listbox>
  );
};
